import React from 'react';

import { Space, Typography } from '@sravni/react-design-system';
import { useBoolean, useIsMobile } from '@sravni/react-utils';

import { ReviewType } from '../../../helpers/reviews';
import { ICommentItem } from '../../../interfaces/reviews';
import { ICommentsListProps } from '../index';

import { Card } from './Card';
import { ShowMoreButton } from './ShowMoreButton';

export interface ICommentProps
  extends Pick<
    ICommentsListProps,
    'reviewId' | 'fetchComments' | 'analyticLabel' | 'reviewTypes' | 'logo' | 'isBankAnswer' | 'scrollToComments'
  > {
  comment: ICommentItem;
  comments: {
    [id: number]: ICommentItem;
  };
  currentUserId: string;
  isLoggedIn: boolean;
  handleCreateComment?: (text: string, parentId: number) => Promise<void>;
  handleUpdateComment?: (text: string, commentId: number, parentId: number) => Promise<void>;
  reviewTypes: ReviewType;
  isRepresentative?: boolean;
  isModerator?: boolean;
  organizationId: string;
}

export const Comment: React.FC<ICommentProps> = ({
  comment,
  comments,
  reviewId,
  currentUserId,
  isLoggedIn,
  fetchComments,
  handleCreateComment,
  handleUpdateComment,
  reviewTypes,
  logo,
  isRepresentative,
  isModerator,
  isBankAnswer,
  scrollToComments,
  organizationId,
}) => {
  const isMobile = useIsMobile();

  const [showComments, setShowComments] = useBoolean(false);
  const [commentsLoading, setCommentsLoading] = useBoolean(false);

  const hasComments = comment.commentsCount > 0;

  const handleOpenComments = () => setShowComments.on();

  const handleShowSecondLevelComments = async () => {
    setCommentsLoading.on();

    await fetchComments({
      ReviewId: reviewId,
      ParentId: comment.id,
      ParentIdCheck: comment.parentId || comment.id,
      PageSize: -1,
      OrganizationId: organizationId,
    });

    setCommentsLoading.off();
    setShowComments.on();
  };

  const cardProps = {
    commentId: comment.id,
    currentUserId,
    isLoggedIn,
    handleCreateComment,
    handleUpdateComment,
    handleOpenComments,
    reviewTypes,
    isRepresentative: isRepresentative || isBankAnswer,
    isModerator,
    isBankAnswer,
  };

  return (
    <div className={'h-mt-32'}>
      <Card key={comment.id} item={comment} {...cardProps} isBankAnswer={isBankAnswer} logo={logo} />
      {isBankAnswer && (
        <Typography.Link className={'h-color-D30'} onClick={scrollToComments}>
          Ответить
        </Typography.Link>
      )}
      {hasComments && showComments && comment.comments?.length
        ? comment.comments.map((key) => {
            const item = comments[key];
            return (
              <Space
                key={`comment-${item.id}`}
                direction={'vertical'}
                style={{ paddingLeft: isMobile ? '16px' : '32px' }}
                className={'h-mt-32'}
              >
                <Card item={item} {...cardProps} logo={logo} />
              </Space>
            );
          })
        : null}
      {hasComments && !showComments ? (
        <ShowMoreButton
          onShowMore={handleShowSecondLevelComments}
          moreAnswers={comment.commentsCount}
          loading={commentsLoading}
        />
      ) : null}
    </div>
  );
};
